import { useId, useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SupplierRecordView } from "@shared-core/data/purchase-ledger-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";

/**
 * Correct a supplier's name (its SUP number never changes). The way to clear
 * a Xero name clash from CIS's side: Xero's contact follows the new name, and
 * an invoice waiting for a supplier of that name is read again. Offered to a
 * caller who may manage suppliers, when the account carries the step's URL.
 */
export function RenameSupplierButton({
  account,
  onChanged,
}: {
  account: SupplierRecordView;
  /** The account as it now stands, re-read after the rename. */
  onChanged: (account: SupplierRecordView) => void;
}) {
  const adapter = useDataAdapter();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(account.name);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);

  if (!account.links.rename || !account.canManage) return null;

  function reset() {
    setName(account.name);
    setReason("");
    setFields({});
    setMessage(null);
  }

  async function submit() {
    setSaving(true);
    setFields({});
    setMessage(null);
    try {
      const updated = await adapter.renameSupplier(account, {
        name: name.trim(),
        reason: reason.trim(),
      });
      toast.success(`${account.supplierNo} is now ${updated.name}`);
      setOpen(false);
      onChanged(updated);
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        setFields(
          Object.fromEntries(fieldErrors.map((e) => [e.field, e.message])),
        );
      } else {
        setMessage(err instanceof Error ? err.message : "The rename failed.");
      }
    } finally {
      setSaving(false);
    }
  }

  const nameError = fields.name;
  const reasonError = fields.reason;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Rename…
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename {account.name}</DialogTitle>
          <DialogDescription>
            {account.supplierNo} keeps its number. Xero&apos;s contact follows
            the new name on its next sync.
            {account.supplierCode &&
              " While Tharstern's supplier load runs, it still overwrites the name of a supplier in Tharstern."}
          </DialogDescription>
        </DialogHeader>
        {(message || Object.keys(fields).length > 0) && (
          <p role="alert" className="text-sm text-red-600">
            {message ?? "Correct what is marked below."}
          </p>
        )}
        <div className="space-y-1.5">
          <label htmlFor={`${id}-name`} className="text-sm font-medium">
            Name
          </label>
          <Input
            id={`${id}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={nameError ? true : undefined}
            aria-describedby={nameError ? `${id}-name-error` : undefined}
          />
          {nameError && (
            <p id={`${id}-name-error`} className="text-sm text-red-600">
              {nameError}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-reason`} className="text-sm font-medium">
            Why
          </label>
          <Textarea
            id={`${id}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Xero already has a contact called 123-reg; ours is 123-reg Ltd"
            aria-invalid={reasonError ? true : undefined}
            aria-describedby={reasonError ? `${id}-reason-error` : undefined}
          />
          {reasonError && (
            <p id={`${id}-reason-error`} className="text-sm text-red-600">
              {reasonError}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              saving ||
              name.trim() === "" ||
              name.trim() === account.name ||
              reason.trim() === ""
            }
          >
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
